import PropTypes, { object } from 'prop-types';
import { useCallback, useEffect, useMemo, useState, Fragment, useRef } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import {
  useMediaQuery,
  Button,
  Chip,
  Dialog,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TextField,
  Drawer,
  Box
} from '@mui/material';

// third-party

// project import
// import CustomerView from 'sections/apps/customer/CustomerView';

import MainCard from 'components/MainCard';

import useMain from 'hooks/useMain';
import { sharedMainGridColumns, sharedSummaryGridColumns } from './column/table-columns';
import CusMultipleFilter from 'components/CusMultipleFilter';
import CusDataGrid from 'components/CusDataGrid/index';
import { AdvancedFilter, ModuleCount, SidebarForm } from './component';
import { BC_TYPE } from './constant';
import CusDrawer from 'components/CusDrawer';
import { filterWorkflowCount } from './workflow-count-data';
import { advancedFilterFields, moduleFilterFields } from './field';
import { getStoredSelectedData, rFetchCategories, setState } from 'store/reducers/comModule';
import { handleAsyncCall, mapListToOptions, OptionUtils, toDefaultDateFormat } from 'utils/common';
import { micService } from 'services/mic';
import _, { result } from 'lodash';
import { useData, useFetchData } from './hook/useData';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { makeStyles } from '@mui/styles';
import { Button1 } from 'components/Button';

const useStyles = makeStyles({
  bgColor: {
    backgroundColor: '#f3f6f5'
  }
});

const ModuleLayout = () => {
  const theme = useTheme();
  const multiFilterRef = useRef(null);
  const sideFormFilterRef = useRef(null);
  const [openSiderbar, setOpenSidebar] = useState(false);
  const [summary, setSummary] = useState([]);
  const [data, setData] = useState([]);
  const { t, useNavigate, useLocation, dispatch, useSelector, project, upperCaseLang, setRowSelected, rowSelected } = useMain();
  const navigate = useNavigate();
  const {
    supplierOptions,
    categoryOptions,
    subCategoryOptions,
    blockOptions,
    floorOptions,
    unitOptions,
    concreteOptions,
    rsCompanyOptions,
    selectedRegion,
    selectedFloor,
    currentBctype,
    setSelectedFloor,
    targetTypeSteps,
    setSelectedRegion,
    isLoading,
    setLoading,
    savedFilterData,
    setSavedFilterData
  } = useData({
    forFilter: true
  });

  useFetchData({
    forFilter: true
  });

  const classes = useStyles();
  const [filterLabel, setFilterLabel] = useState({});
  const [rowsCount, setRowsCount] = useState(null);

  const navigateToDetail = (id, rfidtagNo) => {
    navigate(`/bc/${currentBctype?.toLowerCase()}/detail`, {
      state: {
        id,
        rfidtagNo
      }
    });
  };

  const fetchListData = (_data) => {
    let newData;
    const entries = Object.entries(_data)
      .map(([k, v]) => {
        if (k.includes('Date')) {
          if (v?.startDate) {
            return [k, [toDefaultDateFormat(v.startDate), toDefaultDateFormat(v.endDate)]];
          }
          return [];
        }

        if (k === 'ChipFailureSa' || k === 'ChipFailureSi' || k === 'Dispose') {
          return [k, v ? 1 : 0];
        }

        if (v) {
          return [k, v];
        }

        return [];
      })
      .filter((arr) => !_.isEmpty(arr));

    newData = Object.fromEntries(entries);

    setFilterLabel(newData);

    const nData = { ...newData, Dispose: newData?.Dispose ? 1 : null };
    handleAsyncCall({
      service: micService.getRfidListWithFilters,
      params: {
        projid: project.ProjId,
        data: { ...nData, Bctype: [currentBctype] }
      },
      successCb: (res) => {
        setData(res.data);
      }
      // setDetailBlock: setLoading
    });
  };
  const selectedRows = useSelector(getStoredSelectedData);

  useEffect(() => {
    const newBctype = Object.values(BC_TYPE).find((type) => {
      return location.pathname.match(type.toLowerCase());
    });
    if (project?.ProjId && currentBctype === newBctype) {
      fetchListData(savedFilterData);
    }
  }, [currentBctype, project?.ProjId, location.pathname]);

  useEffect(() => {
    if (currentBctype && project?.ProjId) {
      handleAsyncCall({
        service: micService.getBcAccumulates,
        params: {
          projid: project.ProjId,
          bctype: currentBctype
        },
        successCb: (res) => {
          const mappedData =
            res.data.map(({ ThisMonth, ThisWeek, Total, ...rest }) => ({
              Step: `${rest[`StepDesc${upperCaseLang}`]}`,
              ThisMonth,
              ThisWeek,
              Total,
              ...rest
            })) || [];
          setSummary(mappedData);
        },
        setDetailBlock: setLoading
      });
    }
  }, [currentBctype, project?.ProjId]);

  useEffect(() => {
    const localeSummary = summary.map((rest) => ({
      ...rest,
      Step: `${rest[`StepDesc${upperCaseLang}`]}`
    }));
    setSummary(localeSummary);
  }, [upperCaseLang]);
  const handleSubmitFilter = (d) => {
    const multipleFilterValues = multiFilterRef.current.getValues();
    if (_.isObject(d) && !_.isEmpty(multipleFilterValues)) {
      if (Object.keys(d)?.find((k) => k === 'ASN')) {
        setSavedFilterData(d);
      }
      const data = { ...savedFilterData, ...d, ...multipleFilterValues };

      fetchListData(data);
      setOpenSidebar(false);
    }
  };

  useEffect(() => {
    setOpenSidebar(false);

    multiFilterRef?.current?.reset();
    sideFormFilterRef?.current?.reset();
  }, [currentBctype]);

  const onRowClick = ({ row, isBatchUpdate }) => {
    dispatch(setState(['selectedData', row]));

    return !isBatchUpdate && navigateToDetail(row.Id, row.RfidtagNo);
  };

  const advancedFilterFieldsResult = advancedFilterFields({
    t,
    type: currentBctype,
    categoryOptions,
    concreteOptions,
    rsCompanyOptions,
    subCategoryOptions,
    supplierOptions,
    blockOptions,
    floorOptions,
    unitOptions: mapListToOptions(unitOptions, 'label', 'label'),

    upperCaseLang
  });

  const renderLabel = ({ data, fields }) => {
    return (
      <Box mt={2}>
        <Stack direction="row" spacing={2}>
          {data &&
            Object.keys(data).map((key) => {
              let label = '';
              let value = data[key];
              if (_.isEqual(key, 'Dispose')) {
                if (!data[key]) {
                  return;
                }
                value = t('YES');
              }
              if (_.isEqual(key, 'SerialNo') || _.isEqual(key, 'RfidtagNo')) {
                return;
              }
              const findResult = _.find(fields, ({ id }) => _.isEqual(id, key));

              if (findResult?.label) {
                label = findResult?.label;
              } else {
                label = t(key.toUpperCase());
              }

              if (_.isArray(data[key])) {
                if (key.match(/RscompanyId/gi)) {
                  value = data[key].map((id) => _.find(rsCompanyOptions, ({ value }) => value === id)?.label).join(', ');
                } else if (key.match(/date/gi)) {
                  value = data[key].join(' - ');
                } else {
                  value = data[key].join(', ');
                }
              }

              // if(_.isEqual(findResult?.type,'checkbox')){
              // }

              return (
                <Chip
                  key={key}
                  label={`${label}: ${value}`}
                  sx={{ backgroundColor: theme.palette.primary.lighter, borderRadius: '6px', color: theme.palette.primary[900] }}
                />
              );
            })}
        </Stack>
      </Box>
    );
  };

  return (
    <>
      <MainCard sx={{ mb: '32px' }}>
        <Stack>
          {/* add theme color */}
          <Typography display="inline-block" mb={5} variant="h4" color={theme.palette.primary[400]}>{`${t(currentBctype)} - ${t(
            'SUMMARY'
          )} `}</Typography>

          <CusDataGrid
            loading={isLoading}
            components={{ Toolbar: null }}
            disableSelectionOnClick
            getRowId={(row) => row.Step}
            rows={summary}
            hideFooter
            disableColumnMenu
            columns={sharedSummaryGridColumns({ t })}
          />
        </Stack>
      </MainCard>
      {/* <ModuleCount counts={filterWorkflowCount(currentBctype)} /> */}
      <MainCard content={false} sx={{ mb: '32px' }}>
        <CusMultipleFilter
          ref={multiFilterRef}
          handleSubmit={handleSubmitFilter}
          filters={moduleFilterFields({
            t
          })}
          renderLabel={renderLabel({ data: filterLabel, fields: advancedFilterFieldsResult })}
          toggleDrawer={() => {
            setOpenSidebar(!openSiderbar);
          }}
        />
      </MainCard>
      <MainCard>
        {/* <Box height={'500px'} width="100%"> */}
        <CusDataGrid
          loading={isLoading}
          onSelectionModelChange={(ids) => {
            setRowsCount(ids.length);

            const targetRows = data.filter(({ Id }) => ids.includes(Id));
            dispatch(setState(['selectedData', targetRows]));
          }}
          showExport={false}
          checkboxSelection
          disableSelectionOnClick
          cusToolbarComponents={() => (
            <>
              {/* add theme, outline, radius, endIcon */}
              {rowsCount > 0 && (
                <Typography display="flex" alignItems="center" color="#bfbfbf">
                  {t('ROWS_COUNT', { ROWSCOUNT: rowsCount })}
                </Typography>
              )}

              <Button1
                text={t('BATCH_UPDATE')}
                disabled={_.isEmpty(selectedRows)}
                sx={{ ml: [0, 2], mt: [2, 'auto'] }}
                onClick={() => {
                  navigate(`/bc/${currentBctype?.toLowerCase()}/batchUpdate`);
                }}
                endIcon={!_.isEmpty(selectedRows) && <KeyboardArrowRightIcon />}
              />
            </>
          )}
          getRowId={(row) => row.Id}
          rows={data}
          columns={sharedMainGridColumns({ currentBctype, theme, t, handleToDetails: navigateToDetail, project })}
          custRowClass={(params) => {
            return params?.indexRelativeToCurrentPage % 2 === 1 ? classes.bgColor : '';
          }}
          onRowClick={onRowClick}
        />
        <CusDrawer
          open={openSiderbar}
          onClose={() => {
            setOpenSidebar(false);
            setSavedFilterData(sideFormFilterRef?.current?.getValues());

            // setSavedFilterData({});
          }}
        >
          <SidebarForm
            title={t('ADVANCED_SEARCH')}
            buttonLabel={t('ADVANCED_SEARCH')}
            showClearButton
            formData={savedFilterData}
            handleSubmit={handleSubmitFilter}
            handleClear={() => {
              setSavedFilterData({});
              setFilterLabel({});
              multiFilterRef?.current?.reset();
            }}
            ref={sideFormFilterRef}
            fields={advancedFilterFieldsResult}
            formsLayout={[
              ['Category', 'Dispose'],
              ['ASN'],
              ['ManufacturingDate'],
              ['SerialNo'],
              ['DeliveryDate'],
              ['BatchNo'],
              ['SiteArrivalDate'],
              ['SiteInstallationDate'],
              ['Region'],
              ['Floor'],
              ['RoomNo']
            ]}
          />
        </CusDrawer>
      </MainCard>
    </>
  );
};

export default ModuleLayout;
